/**
 * FirstVisitCheckIn — the primary action on a brand-new user's first
 * home screen. Same options + icons as DailyCheckIn, different framing:
 * "What's hardest right now?". Tapping an option:
 *   1. records the normal daily check-in (existing funnel stays intact)
 *   2. fires a distinct `first_session_checkin` event
 *   3. shows the founder-written response (3-4 sentences + follow-up)
 *      inline — no form, no redirect — then offers intake as a next step.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, MessageCircle, ArrowRight } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";
import { stripPlaceholder } from "@/lib/safeContent";
import { OPTIONS, DailyCheckInResponse } from "@/components/DailyCheckIn";

export default function FirstVisitCheckIn({ onTapped }) {
    const [content, setContent] = useState({});
    const [picked, setPicked] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // The daily-checkin API returns the full clinical response (next steps,
    // when to call the doctor incl. 911 lines). It MUST be rendered here too
    // — the first visit and a reload can never give two different answers.
    const [clinical, setClinical] = useState(null);

    useEffect(() => {
        api.get("/first-checkin-content")
            .then((r) => setContent(r.data?.items || {}))
            .catch(() => { /* placeholders unavailable — fallback below */ });
    }, []);

    const tap = async (code) => {
        if (submitting || picked) return;
        setSubmitting(true);
        setPicked(code);
        try { track(EVENTS.FIRST_SESSION_CHECKIN, { code }); } catch (_e) { /* best-effort */ }
        try {
            const r = await api.post("/me/daily-checkin", { code });
            setClinical(r.data?.response || null);
            try { track(EVENTS.DAILY_CHECK_IN_COMPLETED, { code }); } catch (_e) { /* best-effort */ }
        } catch (_e) { /* voluntary — response still shows */ }
        setSubmitting(false);
        onTapped?.();
    };

    const r = picked ? content[picked] : null;
    const pickedOpt = picked ? OPTIONS.find((o) => o.code === picked) : null;

    if (picked) {
        return (
            <div className="space-y-4">
            <section
                data-testid="first-visit-checkin-response"
                className="rounded-3xl border border-[#D5E6E4] p-5 md:p-7 ml-v2-soft-shadow"
                style={{ background: "linear-gradient(135deg, #FDFBF4 0%, #F3F8F5 100%)" }}
            >
                <div className="flex items-start gap-3">
                    <span className="w-11 h-11 rounded-2xl bg-teal-soft flex items-center justify-center shrink-0">
                        <MessageCircle size={18} strokeWidth={1.8} className="text-teal-dark" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark">
                            You picked: {pickedOpt?.label || picked}
                        </p>
                        <p className="mt-2 text-[#1F2624] leading-relaxed" data-testid="first-visit-response-text">
                            {stripPlaceholder(r?.text) || "Lane hears you. This is one of the most common things caregivers bring up, and there are practical things that usually help. Keep noting what you see — patterns show up faster than you'd expect, and Lane will point them out."}
                        </p>
                        <p className="mt-3 font-medium text-[#12776F] leading-relaxed" data-testid="first-visit-response-followup">
                            {stripPlaceholder(r?.follow_up) || "What would feel most helpful to talk through first?"}
                        </p>
                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            <Link
                                to={`/chat?prompt=${encodeURIComponent(r?.follow_up || "")}`}
                                data-testid="first-visit-answer-lane"
                                className="inline-flex items-center gap-2 rounded-2xl bg-[#12776F] text-[#FDFBF7] px-5 py-2.5 text-sm font-semibold hover:bg-[#0F615A] transition-colors duration-300"
                            >
                                <MessageCircle size={14} strokeWidth={1.7} /> Answer Lane
                            </Link>
                            <Link
                                to="/intake"
                                data-testid="first-visit-tell-more"
                                className="text-sm font-medium text-[#4F7F95] hover:text-[#12776F] inline-flex items-center gap-1.5 min-h-[44px] transition-colors duration-300"
                            >
                                Tell Lane more about your loved one <ArrowRight size={13} strokeWidth={1.8} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
            {clinical && (
                <DailyCheckInResponse
                    today={{ logged_today: true, label: pickedOpt?.label, response: clinical }}
                />
            )}
            </div>
        );
    }

    return (
        <section
            data-testid="first-visit-checkin"
            className="rounded-3xl border border-[#D5E6E4] p-5 md:p-7 ml-v2-soft-shadow"
            style={{ background: "linear-gradient(135deg, #FDFBF4 0%, #F3F8F5 100%)" }}
        >
            <div className="flex items-start gap-3">
                <span className="w-11 h-11 rounded-2xl bg-teal-soft flex items-center justify-center shrink-0">
                    <Sparkles size={18} strokeWidth={1.8} className="text-teal-dark" />
                </span>
                <div className="flex-1 min-w-0">
                    <h2
                        data-testid="first-visit-checkin-heading"
                        className="font-heading text-xl md:text-2xl text-[#2D3748] leading-snug"
                    >
                        What&apos;s hardest right now?
                    </h2>
                    <p className="mt-1 text-sm text-[#4C5551]" data-testid="first-visit-checkin-subtext">
                        Tap one. Lane will tell you what usually helps.
                    </p>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="first-visit-checkin-options">
                        {OPTIONS.map((opt) => (
                            <button
                                key={opt.code}
                                type="button"
                                onClick={() => tap(opt.code)}
                                disabled={submitting}
                                data-testid={`first-visit-option-${opt.code}`}
                                className="flex items-center gap-2.5 text-left text-sm min-h-[48px] rounded-2xl px-3.5 py-2.5 border bg-white text-[#2D3748] border-[#E4DECE] hover:border-teal hover:bg-teal-soft/40 hover:shadow-sm transition-all disabled:opacity-60"
                            >
                                <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 bg-[#F3F8F5]">
                                    <opt.icon size={15} strokeWidth={2} className="text-teal-dark" />
                                </span>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
